import * as react from 'react';
import { useCallback, useMemo } from 'react';

import type Player from 'video.js/dist/types/player';

import { useHandleSyncPlaybackControls } from 'bundles/video-trim/hooks/videoPlayerHooks';
import { getSyncPlayerOnTimeUpdate, syncPlayerDuration } from 'bundles/video-trim/utils/videoTrimmingPlayerSyncUtils';
import {
  getVideoStartTimeOffsetSeconds,
  getVideoTrimmedDurationSeconds,
} from 'bundles/video-trim/utils/videoTrimmingUtils';

export const useSyncVideoTrimmingWithPlayer = ({
  player,
  startTimeMs,
  endTimeMs,
}: {
  player: Player | null;
  startTimeMs: number | null;
  endTimeMs: number | null;
}) => {
  const startTime = useMemo(() => getVideoStartTimeOffsetSeconds(startTimeMs), [startTimeMs]);

  const duration = useMemo(() => getVideoTrimmedDurationSeconds(startTimeMs, endTimeMs), [endTimeMs, startTimeMs]);

  useHandleSyncPlaybackControls({
    videoJs: player,
    start: startTime,
  });

  const syncPlayer = useCallback<() => $TSFixMe>(() => {
    const syncPlayerFn = getSyncPlayerOnTimeUpdate({
      player,
      trimmedDurationSeconds: duration,
      trimmedStartTimeSeconds: startTime,
    });
    if (duration) {
      syncPlayerFn();
    }
  }, [duration, player, startTime]);

  const updateStartTime = useCallback<() => $TSFixMe>(() => {
    if (startTime && player) {
      player?.currentTime(startTime);
    }
  }, [player, startTime]);

  const updateDurationAndStartTime = useCallback<() => $TSFixMe>(() => {
    if (player) {
      syncPlayerDuration(player, duration);
      updateStartTime();
    }
  }, [duration, player, updateStartTime]);

  return {
    onTimeUpdate: syncPlayer,
    onDurationChange: updateStartTime,
    onLoadedMetadata: updateDurationAndStartTime,
  };
};
